import { supabase } from './supabaseClient';
import { calcularNivelLiderazgo } from '../utils/nivelLiderazgo';

// Las URLs de Supabase Storage siempre son absolutas.
export const getImageUrl = (imagePath) => imagePath || null;

export const usuariosAPI = {
  obtenerPorId: async (id) => {
    const { data: perfil, error } = await supabase
      .from('usuarios')
      .select('*')
      .eq('id', id)
      .single();
    if (error) throw error;

    const { data: ultimaEval } = await supabase
      .from('evaluaciones')
      .select('puntuacion_total')
      .eq('usuario_id', id)
      .eq('completada', true)
      .order('fecha_evaluacion', { ascending: false })
      .limit(1)
      .maybeSingle();

    const puntuacion = ultimaEval?.puntuacion_total ?? null;

    return {
      data: {
        ...perfil,
        nivel_liderazgo: calcularNivelLiderazgo(puntuacion),
        puntuacion: puntuacion != null ? Math.round(puntuacion * 100) / 100 : 0.0,
      },
    };
  },

  actualizarParcial: async (id, data) => {
    const { data: row, error } = await supabase
      .from('usuarios')
      .update(data)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return { data: row };
  },

  obtenerHistorial: async (id) => {
    const { data, error } = await supabase
      .from('evaluaciones')
      .select('*')
      .eq('usuario_id', id)
      .order('fecha_evaluacion', { ascending: false });
    if (error) throw error;
    return { data };
  },

  obtenerEvaluacionInconclusa: async (id) => {
    const { data, error } = await supabase
      .from('evaluaciones')
      .select('*')
      .eq('usuario_id', id)
      .eq('completada', false)
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    // Compatibilidad: Dashboard.js lee `.data.evaluacion`, Evaluacion.js lee `.data.id`.
    return { data: { evaluacion: data ?? null, ...(data ?? {}) } };
  },
};

const mapEvaluacionPayload = ({ usuario, ...resto }) => ({
  ...resto,
  ...(usuario !== undefined ? { usuario_id: usuario } : {}),
});

export const evaluacionesAPI = {
  crear: async (data) => {
    const { data: row, error } = await supabase
      .from('evaluaciones')
      .insert(mapEvaluacionPayload(data))
      .select()
      .single();
    if (error) throw error;
    return { data: row };
  },

  actualizarParcial: async (id, data) => {
    const { data: row, error } = await supabase
      .from('evaluaciones')
      .update(mapEvaluacionPayload(data))
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return { data: row };
  },

  eliminar: async (id) => {
    const { error } = await supabase.from('evaluaciones').delete().eq('id', id);
    if (error) throw error;
    return { data: null };
  },
};

// Las funciones rpc validan el rol en la base (ver supabase/admin_panel.sql);
// el frontend solo oculta la UI.
export const adminAPI = {
  miRol: async () => {
    const { data, error } = await supabase.rpc('mi_rol');
    if (error) throw error;
    return { data };
  },

  resumen: async () => {
    const { data, error } = await supabase.rpc('admin_resumen').single();
    if (error) throw error;
    return { data };
  },

  // Paginado en la base (ver supabase/admin_panel.sql). Cada fila trae total_count.
  listarUsuarios: async ({ busqueda, rol, orden, asc, limit, offset, usuarioId } = {}) => {
    const { data, error } = await supabase.rpc('admin_listar_usuarios', {
      p_busqueda: busqueda || null,
      p_rol: rol || null,
      p_orden: orden || 'registrado_en',
      p_asc: !!asc,
      p_limit: limit ?? 25,
      p_offset: offset ?? 0,
      p_usuario_id: usuarioId || null,
    });
    if (error) throw error;
    return { data, total: data?.[0]?.total_count ?? 0 };
  },

  listarEvaluaciones: async ({ completada, busqueda, rol, orden, asc, limit, offset } = {}) => {
    const { data, error } = await supabase.rpc('admin_listar_evaluaciones', {
      p_completada: !!completada,
      p_busqueda: busqueda || null,
      p_rol: rol || null,
      p_orden: orden || 'fecha',
      p_asc: !!asc,
      p_limit: limit ?? 25,
      p_offset: offset ?? 0,
    });
    if (error) throw error;
    return { data, total: data?.[0]?.total_count ?? 0 };
  },

  eliminarEvaluacion: async (evaluacionId) => {
    const { error } = await supabase.rpc('admin_eliminar_evaluacion', { p_evaluacion_id: String(evaluacionId) });
    if (error) throw error;
    return { data: null };
  },

  eliminarUsuario: async (usuarioId) => {
    const { error } = await supabase.rpc('admin_eliminar_usuario', { p_usuario_id: usuarioId });
    if (error) throw error;
    return { data: null };
  },

  cambiarRol: async (usuarioId, rol) => {
    const { error } = await supabase.rpc('admin_cambiar_rol', { p_usuario_id: usuarioId, p_rol: rol });
    if (error) throw error;
    return { data: null };
  },
};

export const authAPI = {
  login: async ({ email, password }) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return { data: { token: data.session.access_token, user: { id: data.user.id, email: data.user.email } } };
  },

  register: async ({ nombre, email, password }) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { nombre } },
    });
    if (error) throw error;

    // Correo de bienvenida al usuario + alerta al admin. Nunca debe tumbar el
    // registro si el servicio de correo falla o no está configurado.
    const webApiUrl = process.env.REACT_APP_WEB_API_URL;
    if (webApiUrl) {
      fetch(`${webApiUrl}/api/register-notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nombre, email }),
      }).catch((notifyError) => {
        console.error('No se pudo notificar el registro:', notifyError);
      });
    }

    return { data: { user: data.user ? { id: data.user.id, nombre, email } : null } };
  },

  forgotPassword: async ({ email }) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw error;
    return { data: { message: 'Si el email existe, recibirás instrucciones pronto' } };
  },

  resetPassword: async ({ password }) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
    return { data: { message: 'Contraseña actualizada exitosamente' } };
  },

  logout: async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    return { data: null };
  },
};
